import { LeaveTypeMaster, LeavePolicy, NamedLeavePolicy, LeavePolicyAssignment, AssignmentScopeType, LeavePolicyAllocation, AccrualFrequency } from '../types/leavePolicy';
import { LeaveAccrualRates } from './leaveAccrualEngine';
import { OrgConfigService } from './orgConfigService';
import { generateClient } from 'aws-amplify/api';

const LEAVE_TYPES_KEY = 'creyenteone:leave-types:v1';
const LEAVE_POLICIES_KEY = 'creyenteone:leave-policies:v1';
const NAMED_POLICIES_KEY = 'creyenteone:leave-named-policies:v1';
const ASSIGNMENTS_KEY = 'creyenteone:leave-assignments:v1';
const LEAVE_SETTINGS_KEY = 'creyenteone:leave-settings:v1';

/**
 * Organisation-wide leave settings, global rather than per leave type.
 *
 * Applying for leave is deliberately unrestricted — employees may request past, present or
 * future dates. The lock period governs only how long they keep control of a request they
 * already submitted.
 */
export interface LeaveSettings {
  /**
   * Days of grace after a leave starts during which the employee may still edit or delete it.
   *
   * An employee can always change a request up to the day before it begins; this setting
   * extends that window past the start date. The deadline is exclusive:
   *
   *   deadline = startDate + lockPeriodDays
   *   employee may change it while today < deadline
   *
   *   0 → editable up to the day before; nothing on the leave date itself
   *   2 → leave on 20 Aug stays editable on the 20th and 21st, locked from the 22nd
   *
   * Once it expires only HR and Super Admin can make changes.
   */
  lockPeriodDays: number;
  /** Whether employees may edit or delete their own requests at all. */
  allowEmployeeDeletion: boolean;
  /**
   * How long a Comp-Off day stays usable, in days from the date worked.
   *
   * This is a *validity window*, not a ceiling on the balance. A day worked on 1 March with a
   * 45-day window must be taken by 15 April; after that it lapses and stops counting toward
   * the balance. HR owns the number, hence the setting.
   *
   * It was previously read as "maximum accumulated balance" — the same 45, meaning something
   * entirely different. Under that reading nothing ever expired (the stored expiryDate was
   * never checked) and the employee was shown a progress bar filling toward 45 as though it
   * were a target. See compOffMaxDays below for how old saved settings are carried over.
   */
  compOffValidityDays: number;
  /**
   * Superseded by compOffValidityDays. Retained only so settings saved under the old name
   * still resolve — getSettings falls back to it. Never write it.
   */
  compOffMaxDays?: number;
}

export const DEFAULT_LEAVE_SETTINGS: LeaveSettings = {
  lockPeriodDays: 0,
  allowEmployeeDeletion: true,
  compOffValidityDays: 45,
};

/**
 * The shipped leave types.
 *
 * `color` is the single source of truth for how a leave type is shown *everywhere* — the leave
 * screens read it via leaveTypeColor, and the Shift Roster derives its cell tones from the same
 * value (see shiftRosterDisplay.leaveSeedColor). There is deliberately no second colour table.
 *
 * Earned, Maternity and Paternity were three near-identical blues (#3b82f6 / #8b5cf6 / #0ea5e9):
 * once rendered as roster chips, Earned sat 57 from Paternity and 83 from Maternity, i.e. below
 * the threshold at which a reader can tell them apart. The replacements below were chosen by
 * maximising the minimum pairwise distance across *every* leave chip in both themes — the worst
 * pair is now 108 — while staying clear of Sick (#ef4444), Unpaid and Comp-Off. See
 * LEAVE_COLOUR_SEPARATION for how existing saved configurations are brought forward.
 */
export const DEFAULT_LEAVE_TYPES: LeaveTypeMaster[] = [
  { id: 'lt-el', code: 'EL', name: 'Earned Leave', description: 'Paid annual vacation leave allotment', color: '#1d4ed8', isPaid: true, isActive: true, grantedPerYear: 12 },
  { id: 'lt-cl', code: 'CL', name: 'Casual Leave', description: 'Paid casual time-off leave allotment', color: '#10b981', isPaid: true, isActive: true, grantedPerYear: 10 },
  { id: 'lt-sl', code: 'SL', name: 'Sick Leave', description: 'Paid medical & health recovery leave', color: '#ef4444', isPaid: true, isActive: true, grantedPerYear: 10 },
  // ML and PL grant 0 by default — see ZERO_GRANT_CODES below for why, and for the
  // migration that applies the same to browsers holding an older saved list.
  { id: 'lt-ml', code: 'ML', name: 'Maternity Leave', description: 'Paid statutory maternity leave allotment', color: '#38bdf8', isPaid: true, isActive: true, grantedPerYear: 0 },
  { id: 'lt-co', code: 'CO', name: 'Comp-Off', description: 'Compensatory off for working on weekends or holidays', color: '#f59e0b', isPaid: true, isActive: true, grantedPerYear: 0 },
  // Policy-driven like ML/CO (monthly-prorated from grantedPerYear via the generic branch
  // in leaveBalanceService.ts) — not formula-driven, so no leaveAccrualEngine change needed.
  { id: 'lt-pl', code: 'PL', name: 'Paternity Leave', description: 'Paid leave for new fathers (1 week)', color: '#4d7c0f', isPaid: true, isActive: true, grantedPerYear: 0 },
];

export const DEFAULT_NAMED_POLICIES: NamedLeavePolicy[] = [
  {
    id: 'npol-fulltime',
    name: 'Full-Time Policy',
    description: 'Standard policy for full-time confirmed employees',
    /*
     * These EL/CL/SL numbers are REAL: leaveAccrualEngine takes its annual caps from the
     * policy claiming the employee, prorated from their Date of Joining.
     *
     * They match the engine's built-in Full Time caps on purpose. They read 6/2/2 under a
     * note saying the values were unused and kept "for display purposes" — true while the
     * schedule was hardcoded, and a trap the moment the policy began driving it: a fresh
     * install would have cut every full-time employee's entitlement to a fifth of the
     * casual and sick leave the engine itself defaults to, with nothing to show why.
     */
    allocations: [
      { leaveTypeCode: 'EL', grantedPerYear: 12 },
      { leaveTypeCode: 'CL', grantedPerYear: 10 },
      { leaveTypeCode: 'SL', grantedPerYear: 10 },
      // 0, and it has to be 0 here as well as on the leave type: an assigned policy's
      // allocation *overrides* the leave-type default (see leaveBalanceService —
      // `assignedAllocation?.grantedPerYear ?? leaveType.grantedPerYear`), so leaving 180
      // here would have kept granting Maternity Leave to every full-time employee no
      // matter what the leave type said.
      { leaveTypeCode: 'ML', grantedPerYear: 0 },
      { leaveTypeCode: 'CO', grantedPerYear: 0 },
      { leaveTypeCode: 'PL', grantedPerYear: 0 },
    ],
    accrualFrequency: 'YEARLY',
    carryForwardAllowed: true,
    maxCarryForward: 5,
    expiryMonths: 0,
    allowNegativeBalance: false,
    probationRestricted: false,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'npol-intern',
    name: 'Intern Policy',
    description: 'Reduced leave policy for interns and trainees',
    allocations: [
      { leaveTypeCode: 'EL', grantedPerYear: 0 },
      { leaveTypeCode: 'CL', grantedPerYear: 4 },
      { leaveTypeCode: 'SL', grantedPerYear: 4 },
      { leaveTypeCode: 'ML', grantedPerYear: 0 },
      { leaveTypeCode: 'CO', grantedPerYear: 0 },
      { leaveTypeCode: 'PL', grantedPerYear: 0 },
    ],
    accrualFrequency: 'MONTHLY',
    carryForwardAllowed: false,
    maxCarryForward: 0,
    expiryMonths: 12,
    allowNegativeBalance: false,
    probationRestricted: true,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'npol-consultant',
    name: 'Consultant Policy',
    description: 'Policy for consultants and contract-based staff',
    // Starting point only — fully editable like every other policy. Mirrors the Intern
    // Policy's reduced CL/SL allowance since consultants, like interns, are not on the
    // standard full-time entitlement; adjust freely via Edit.
    allocations: [
      { leaveTypeCode: 'EL', grantedPerYear: 0 },
      { leaveTypeCode: 'CL', grantedPerYear: 4 },
      { leaveTypeCode: 'SL', grantedPerYear: 4 },
      { leaveTypeCode: 'ML', grantedPerYear: 0 },
      { leaveTypeCode: 'CO', grantedPerYear: 0 },
      { leaveTypeCode: 'PL', grantedPerYear: 0 },
    ],
    accrualFrequency: 'MONTHLY',
    carryForwardAllowed: false,
    maxCarryForward: 0,
    expiryMonths: 12,
    allowNegativeBalance: false,
    probationRestricted: false,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export const DEFAULT_LEAVE_POLICIES: LeavePolicy[] = [
  // NOTE: EL, CL, and SL entitlements are now computed by leaveAccrualEngine.ts using
  // the DOJ-based earnedMonths formula. The grantedPerYear values below are used only
  // for ML, CO, and any custom leave types. For EL/CL/SL they serve as display labels only.
  { id: 'pol-el', policyName: 'Standard Earned Leave Policy', leaveTypeCode: 'EL', grantedPerYear: 6, maxCarryForward: 5, allowNegative: false, maxConsecutiveDays: 10, noticeDaysRequired: 3, genderEligibility: 'ALL', expiresOnDec31: false, allowEncashment: true, encashmentMaxDays: 10, updatedAt: new Date().toISOString() },
  { id: 'pol-cl', policyName: 'Standard Casual Leave Policy', leaveTypeCode: 'CL', grantedPerYear: 2, maxCarryForward: 0, allowNegative: false, maxConsecutiveDays: 3, noticeDaysRequired: 1, genderEligibility: 'ALL', expiresOnDec31: true, allowEncashment: false, encashmentMaxDays: 0, updatedAt: new Date().toISOString() },
  { id: 'pol-sl', policyName: 'Standard Sick Leave Policy', leaveTypeCode: 'SL', grantedPerYear: 2, maxCarryForward: 0, allowNegative: true, maxConsecutiveDays: 5, noticeDaysRequired: 0, genderEligibility: 'ALL', expiresOnDec31: true, allowEncashment: false, encashmentMaxDays: 0, updatedAt: new Date().toISOString() },
  // 0 to agree with the leave type and the named policies. This flat list no longer feeds
  // the balance (only the legacy LeavePolicyModal reads it), but a 180 sitting here would
  // still read as the entitlement to anyone opening it.
  { id: 'pol-ml', policyName: 'Statutory Maternity Leave Policy', leaveTypeCode: 'ML', grantedPerYear: 0, maxCarryForward: 0, allowNegative: false, maxConsecutiveDays: 180, noticeDaysRequired: 15, genderEligibility: 'FEMALE', expiresOnDec31: false, allowEncashment: false, encashmentMaxDays: 0, updatedAt: new Date().toISOString() },
  { id: 'pol-co', policyName: 'Compensatory Off Policy', leaveTypeCode: 'CO', grantedPerYear: 0, maxCarryForward: 5, allowNegative: false, maxConsecutiveDays: 2, noticeDaysRequired: 1, genderEligibility: 'ALL', expiresOnDec31: false, allowEncashment: true, encashmentMaxDays: 5, updatedAt: new Date().toISOString() },
];

/**
 * Leave types granted 0 days for everyone.
 *
 * Maternity and Paternity Leave are not accrued entitlements here: they are granted case by
 * case when someone actually needs them, via an HR adjustment, rather than sitting in every
 * employee's balance all year. Carrying 180 ML days and 7 PL days by default overstated the
 * total leave liability for the whole company, and — because gender is not stored on the
 * employee record, so genderEligibility can never be evaluated (see leaveBalanceService) —
 * put 180 days of Maternity Leave in front of every employee regardless.
 *
 * Zeroing the grant leaves both types active and visible at 0, so HR can still see them and
 * still raise a specific adjustment when it is needed.
 */
const ZERO_GRANT_CODES = ['ML', 'PL'];

/**
 * Marks that the ML/PL zeroing has been applied to this browser's saved config.
 *
 * Needed because the defaults above are only consulted when nothing has been saved yet:
 * getLeaveTypes and getNamedPolicies read localStorage first, and seedUnseenDefaults
 * deliberately only *adds* unseen defaults rather than overwriting existing ones. Without
 * this, anyone who had ever opened Leave Policies would keep their stored 180/7 and the
 * change would appear to do nothing.
 *
 * One-time by design: it corrects the old shipped values once, then never fights HR again,
 * so a deliberate non-zero ML set later is respected. Bump the version to re-run it.
 */
const ML_PL_ZEROED_KEY = 'creyenteone:leave-ml-pl-zeroed:v1';

/**
 * The three shipped colours being separated, and what replaces each.
 *
 * Keyed by the *old* value as well as the code so the correction only ever touches a type still
 * carrying the shipped blue. An HR admin who has deliberately chosen a colour keeps it — the same
 * "never fight HR twice" rule ZERO_GRANT_CODES follows.
 */
const LEAVE_COLOUR_SEPARATION: Record<string, { from: string; to: string }> = {
  EL: { from: '#3b82f6', to: '#1d4ed8' },
  ML: { from: '#8b5cf6', to: '#38bdf8' },
  PL: { from: '#0ea5e9', to: '#4d7c0f' },
};

/**
 * Marks that the colour separation has been applied to this browser's saved config.
 *
 * Needed for exactly the reason ML_PL_ZEROED_KEY is: DEFAULT_LEAVE_TYPES is consulted only when
 * nothing has been saved, and seedUnseenDefaults only *adds* unseen types. Without this, anyone
 * who had ever opened Leave Policies would keep the old blues and the roster would still show
 * Earned, Maternity and Paternity as three shades of the same colour.
 */
const LEAVE_COLOURS_SEPARATED_KEY = 'creyenteone:leave-colours-separated:v1';

/** Records which shipped defaults a browser has already been offered. */
const SEEDED_TYPES_KEY = 'creyenteone:leave-types-seeded:v1';
const SEEDED_POLICIES_KEY = 'creyenteone:leave-policies-seeded:v1';

/**
 * Server-backed leave configuration.
 *
 * Every store below — leave types, leave policies, named policies, policy assignments and
 * org-wide settings — was localStorage-only, which meant HR renaming a leave type, changing an
 * entitlement or assigning a policy changed nothing for anyone else: the whole configuration
 * lived in the browser of whoever last edited it. This was the largest remaining gap.
 *
 * The reads stay synchronous. Dozens of call sites treat getLeaveTypes() / getSettings() as
 * cheap, and several are inside pure computation (leaveBalanceService.computeSnapshotsFor most
 * notably), so localStorage becomes a cache of the server: `refreshConfig()` fills it and
 * `persistConfig()` writes through to the server in the background.
 */
const CONFIG_SYNC_KEYS = [
  LEAVE_TYPES_KEY,
  LEAVE_POLICIES_KEY,
  NAMED_POLICIES_KEY,
  ASSIGNMENTS_KEY,
  LEAVE_SETTINGS_KEY,
] as const;

let configClient: any = null;
const getConfigClient = () => {
  if (!configClient) configClient = generateClient();
  return configClient;
};

const listLeaveConfigQuery = /* GraphQL */ `
  query ListLeaveConfig {
    listLeaveConfig { configKey value updatedBy updatedAt }
  }
`;

const putLeaveConfigMutation = /* GraphQL */ `
  mutation PutLeaveConfig($configKey: String!, $value: AWSJSON!, $updatedBy: String) {
    putLeaveConfig(configKey: $configKey, value: $value, updatedBy: $updatedBy) { configKey }
  }
`;

export class LeavePolicyService {
  /**
   * Pull every configuration document from the server into the local cache.
   *
   * Returns false when the server could not be reached, in which case the cache — which may be
   * this browser's own edits, or the shipped defaults — is what the UI keeps showing.
   */
  static async refreshConfig(): Promise<boolean> {
    try {
      const result = await getConfigClient().graphql({
        query: listLeaveConfigQuery,
        authMode: 'userPool' as const,
      } as any);
      const docs = ('data' in result ? result.data : null)?.listLeaveConfig;
      if (!Array.isArray(docs)) return false;

      let changed = false;
      for (const doc of docs) {
        const key = String(doc?.configKey || '');
        if (!(CONFIG_SYNC_KEYS as readonly string[]).includes(key)) continue;
        // `value` is AWSJSON — a JSON *string* on the wire. It is stored verbatim so the cache
        // holds exactly what every getter already expects to JSON.parse.
        const raw = typeof doc.value === 'string' ? doc.value : JSON.stringify(doc.value);
        if (raw && raw !== localStorage.getItem(key)) {
          localStorage.setItem(key, raw);
          changed = true;
        }
      }

      // Announce only a real change: pages recompute on this event, and an unconditional
      // dispatch would have them trigger the refresh that re-fires it.
      if (changed) window.dispatchEvent(new Event('leave-policy-updated'));
      return true;
    } catch (error) {
      console.warn('[LeavePolicy] config refresh failed, using cached configuration:', error);
      return false;
    }
  }

  /**
   * Write a configuration document to the cache now and to the server in the background.
   *
   * Synchronous because the callers are — createLeaveType, updateNamedPolicy, saveSettings and
   * the rest are called from click handlers that already assume an immediate local effect.
   * The server write is fire-and-forget with a logged failure; the trade-off is that a failed
   * push leaves the edit local until someone retries it, which is still strictly better than
   * the previous behaviour of never leaving the browser at all.
   */
  private static persistConfig(key: string, value: unknown): void {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);

    getConfigClient()
      .graphql({
        query: putLeaveConfigMutation,
        variables: { configKey: key, value: serialized, updatedBy: 'app' },
        authMode: 'userPool' as const,
      } as any)
      .catch((error: unknown) =>
        console.warn(`[LeavePolicy] "${key}" saved locally but not on the server:`, error)
      );
  }

  /**
   * Introduce defaults this browser has never seen, without resurrecting anything.
   *
   * Two traps this avoids, both of which previously looked like "my edit didn't save":
   *
   *  1. Matching on a user-editable field. Keying on `code` (or a policy's `name`) meant
   *     renaming EL to AL made the seeder believe EL had gone missing and add it back —
   *     so the rename appeared to fail and the old code reappeared everywhere. Identity
   *     is the immutable `id`.
   *  2. Re-adding deletions. Absence alone can't distinguish "new default" from
   *     "deliberately deleted", so the ids already offered are recorded; a default is
   *     seeded once and never again.
   */
  private static seedUnseenDefaults<T extends { id: string }>(
    current: T[],
    defaults: T[],
    storageKey: string,
    seededKey: string
  ): T[] {
    let seeded: string[] = [];
    try {
      const raw = localStorage.getItem(seededKey);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) seeded = parsed;
    } catch {
      seeded = [];
    }

    const alreadyOffered = new Set(seeded);
    const presentIds = new Set(current.map(x => x.id));
    const toAdd = defaults.filter(d => !alreadyOffered.has(d.id) && !presentIds.has(d.id));

    // Mark every shipped default as offered, so a later deletion is respected.
    const nextSeeded = Array.from(new Set([...seeded, ...defaults.map(d => d.id)]));
    if (nextSeeded.length !== seeded.length) {
      localStorage.setItem(seededKey, JSON.stringify(nextSeeded));
    }

    if (toAdd.length === 0) return current;
    const next = [...current, ...toAdd];
    this.persistConfig(storageKey, next);
    return next;
  }

  /**
   * Force the ZERO_GRANT_CODES grant to 0 in this browser's saved config, exactly once.
   *
   * Both stores are corrected together, because ML/PL can be granted from either: the leave
   * type's own grantedPerYear, or an assigned named policy's allocation, which takes
   * precedence over it. Fixing only one would leave the other still handing out days.
   *
   * Runs before the getters read, and no-ops entirely once the flag is set — so the cost on
   * every later call is a single localStorage read. A parse failure leaves the stored config
   * untouched rather than replacing it with defaults, since throwing away HR's saved leave
   * types would be far worse than a grant that stayed non-zero.
   */
  /**
   * Replace the three near-identical shipped leave colours in this browser's saved config, once.
   *
   * Only rewrites a type whose colour is still the exact value that shipped, so a deliberate HR
   * choice survives. Goes through persistConfig so the correction reaches the server instead of
   * being re-applied in every browser, and dispatches the same event the other stores use so open
   * screens re-read rather than needing a reload.
   *
   * Failure leaves the stored config untouched: three leave types that look alike is a far better
   * outcome than a wiped leave-type list.
   */
  private static separateLeaveColoursOnce(): void {
    try {
      if (localStorage.getItem(LEAVE_COLOURS_SEPARATED_KEY)) return;

      const raw = localStorage.getItem(LEAVE_TYPES_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          let changed = false;
          const next = parsed.map((t: LeaveTypeMaster) => {
            const rule = LEAVE_COLOUR_SEPARATION[String(t?.code || '').toUpperCase()];
            if (rule && String(t?.color || '').toLowerCase() === rule.from) {
              changed = true;
              return { ...t, color: rule.to };
            }
            return t;
          });
          if (changed) this.persistConfig(LEAVE_TYPES_KEY, next);
        }
      }

      localStorage.setItem(LEAVE_COLOURS_SEPARATED_KEY, new Date().toISOString());
      window.dispatchEvent(new Event('leave-policy-updated'));
    } catch {
      /* Stored config left as-is — see the note above. */
    }
  }

  private static zeroMlPlGrantsOnce(): void {
    try {
      if (localStorage.getItem(ML_PL_ZEROED_KEY)) return;

      const shouldZero = (code: unknown) =>
        ZERO_GRANT_CODES.includes(String(code || '').toUpperCase());

      const rawTypes = localStorage.getItem(LEAVE_TYPES_KEY);
      if (rawTypes) {
        const parsed = JSON.parse(rawTypes);
        if (Array.isArray(parsed)) {
          // Through persistConfig, so the zeroing reaches the server rather than being
          // re-applied separately in every browser.
          this.persistConfig(
            LEAVE_TYPES_KEY,
            parsed.map(t => (shouldZero(t?.code) ? { ...t, grantedPerYear: 0 } : t))
          );
        }
      }

      const rawPolicies = localStorage.getItem(NAMED_POLICIES_KEY);
      if (rawPolicies) {
        const parsed = JSON.parse(rawPolicies);
        if (Array.isArray(parsed)) {
          this.persistConfig(
            NAMED_POLICIES_KEY,
            parsed.map(p => (Array.isArray(p?.allocations)
              ? {
                  ...p,
                  allocations: p.allocations.map((a: { leaveTypeCode?: string }) =>
                    shouldZero(a?.leaveTypeCode) ? { ...a, grantedPerYear: 0 } : a
                  ),
                }
              : p))
          );
        }
      }

      localStorage.setItem(ML_PL_ZEROED_KEY, new Date().toISOString());
      window.dispatchEvent(new Event('leave-policy-updated'));
    } catch {
      /* Stored config left as-is; better a stale grant than a wiped policy list. */
    }
  }

  static getLeaveTypes(): LeaveTypeMaster[] {
    this.zeroMlPlGrantsOnce();
    this.separateLeaveColoursOnce();
    try {
      const raw = localStorage.getItem(LEAVE_TYPES_KEY);
      let types: LeaveTypeMaster[];
      if (!raw) {
        types = DEFAULT_LEAVE_TYPES;
      } else {
        const parsed = JSON.parse(raw);
        types = Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_LEAVE_TYPES;
      }

      // Introduces a newly-shipped default (e.g. Paternity Leave) to a browser that
      // already had its own saved list, without undoing renames or deletions.
      return this.seedUnseenDefaults(types, DEFAULT_LEAVE_TYPES, LEAVE_TYPES_KEY, SEEDED_TYPES_KEY);
    } catch {
      return DEFAULT_LEAVE_TYPES;
    }
  }

  static getPolicies(): LeavePolicy[] {
    try {
      const raw = localStorage.getItem(LEAVE_POLICIES_KEY);
      if (!raw) return DEFAULT_LEAVE_POLICIES;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_LEAVE_POLICIES;
    } catch {
      return DEFAULT_LEAVE_POLICIES;
    }
  }

  static getPolicyForType(leaveTypeCode: string): LeavePolicy | undefined {
    return this.getPolicies().find(p => p.leaveTypeCode.toUpperCase() === leaveTypeCode.toUpperCase());
  }

  static updatePolicy(updatedPolicy: LeavePolicy): void {
    const policies = this.getPolicies();
    const index = policies.findIndex(p => p.id === updatedPolicy.id || p.leaveTypeCode === updatedPolicy.leaveTypeCode);
    let next: LeavePolicy[];
    if (index >= 0) {
      next = [...policies];
      next[index] = { ...updatedPolicy, updatedAt: new Date().toISOString() };
    } else {
      next = [...policies, { ...updatedPolicy, updatedAt: new Date().toISOString() }];
    }
    this.persistConfig(LEAVE_POLICIES_KEY, next);
    window.dispatchEvent(new Event('leave-policy-updated'));
    window.dispatchEvent(new Event('storage'));
  }

  // ---- Leave Types CRUD ----
  private static saveLeaveTypes(types: LeaveTypeMaster[]): void {
    this.persistConfig(LEAVE_TYPES_KEY, types);
    window.dispatchEvent(new Event('leave-policy-updated'));
    window.dispatchEvent(new Event('storage'));
  }

  static createLeaveType(input: Omit<LeaveTypeMaster, 'id'>): LeaveTypeMaster {
    const newType: LeaveTypeMaster = { ...input, id: `lt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` };
    this.saveLeaveTypes([...this.getLeaveTypes(), newType]);
    return newType;
  }

  static updateLeaveType(updated: LeaveTypeMaster): void {
    const types = this.getLeaveTypes();
    const next = types.map(t => (t.id === updated.id ? updated : t));
    this.saveLeaveTypes(next);
  }

  static deleteLeaveType(id: string): void {
    this.saveLeaveTypes(this.getLeaveTypes().filter(t => t.id !== id));
  }

  // ---- Named Leave Policies (multiple named policies with per-type allocations) ----
  static getNamedPolicies(): NamedLeavePolicy[] {
    this.zeroMlPlGrantsOnce();
    try {
      const raw = localStorage.getItem(NAMED_POLICIES_KEY);
      let policies: NamedLeavePolicy[];
      if (!raw) {
        policies = DEFAULT_NAMED_POLICIES;
      } else {
        const parsed = JSON.parse(raw);
        policies = Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_NAMED_POLICIES;
      }

      // Same rule as leave types: seed a new default once, never resurrect a renamed
      // or deleted one. Matching on `name` here is what made policy edits look unsaved.
      return this.seedUnseenDefaults(policies, DEFAULT_NAMED_POLICIES, NAMED_POLICIES_KEY, SEEDED_POLICIES_KEY);
    } catch {
      return DEFAULT_NAMED_POLICIES;
    }
  }

  private static saveNamedPolicies(policies: NamedLeavePolicy[]): void {
    this.persistConfig(NAMED_POLICIES_KEY, policies);
    window.dispatchEvent(new Event('leave-policy-updated'));
    window.dispatchEvent(new Event('storage'));
  }

  static createNamedPolicy(input: Omit<NamedLeavePolicy, 'id' | 'createdAt' | 'updatedAt'>): NamedLeavePolicy {
    const now = new Date().toISOString();
    const newPolicy: NamedLeavePolicy = { ...input, id: `npol-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, createdAt: now, updatedAt: now };
    this.saveNamedPolicies([...this.getNamedPolicies(), newPolicy]);
    return newPolicy;
  }

  static updateNamedPolicy(updated: NamedLeavePolicy): void {
    const policies = this.getNamedPolicies();
    const next = policies.map(p => (p.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : p));
    this.saveNamedPolicies(next);
  }

  static deleteNamedPolicy(id: string): void {
    this.saveNamedPolicies(this.getNamedPolicies().filter(p => p.id !== id));
  }

  // ---- Leave Assignments (assign a named policy to an employee or a scope) ----
  static getAssignments(): LeavePolicyAssignment[] {
    try {
      const raw = localStorage.getItem(ASSIGNMENTS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  static createAssignment(policyId: string, scopeType: AssignmentScopeType, scopeValue: string, assignedBy: string): LeavePolicyAssignment {
    const assignment: LeavePolicyAssignment = {
      id: `assign-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      policyId,
      scopeType,
      scopeValue,
      assignedBy,
      assignedAt: new Date().toISOString(),
    };
    const existing = this.getAssignments();
    this.persistConfig(ASSIGNMENTS_KEY, [assignment, ...existing]);
    window.dispatchEvent(new Event('leave-policy-updated'));
    window.dispatchEvent(new Event('storage'));
    return assignment;
  }

  static deleteAssignment(id: string): void {
    this.persistConfig(ASSIGNMENTS_KEY, this.getAssignments().filter(a => a.id !== id));
    window.dispatchEvent(new Event('leave-policy-updated'));
    window.dispatchEvent(new Event('storage'));
  }

  /**
   * Resolve which Named Policy actually applies to an employee, if any — most specific
   * scope wins: an EMPLOYEE-level assignment beats DESIGNATION, which beats DEPARTMENT,
   * which beats LOCATION, which beats the employee’s EMPLOYMENT TYPE. Returns undefined
   * if nothing matches (or the matched policy has since been deactivated), so callers fall
   * back to the global default.
   *
   * Employment type is last because it is the broadest — it claims everybody of that type
   * who has not been named more precisely. It is also the only one of the five that can be
   * set today: the screen that created the other four assignments was removed, which is why
   * a policy could sit on the Leave Policies page marked ACTIVE and govern nobody at all.
   *
   * This governs ML/CO/custom types, and now EL/CL/SL too when the policy allocates them —
   * see capsFromPolicy and the accrual engine’s header.
   */
  static getEffectivePolicyForEmployee(user: {
    email: string;
    department?: string;
    designation?: string;
    location?: string;
    employmentType?: string;
  }): NamedLeavePolicy | undefined {
    const assignments = this.getAssignments();
    const policiesById = new Map(this.getNamedPolicies().filter(p => p.isActive).map(p => [p.id, p]));

    const findByScope = (scopeType: AssignmentScopeType, value?: string): NamedLeavePolicy | undefined => {
      if (!value) return undefined;
      const match = assignments.find(
        a => a.scopeType === scopeType && a.scopeValue.toLowerCase() === value.toLowerCase()
      );
      return match ? policiesById.get(match.policyId) : undefined;
    };

    return (
      findByScope('EMPLOYEE', user.email) ||
      findByScope('DESIGNATION', user.designation) ||
      findByScope('DEPARTMENT', user.department) ||
      findByScope('LOCATION', user.location) ||
      this.findPolicyForEmploymentType(user.employmentType)
    );
  }

  /**
   * The single active policy governing an employment type, or undefined.
   *
   * Unique or nothing: two policies claiming "Intern" is a configuration mistake, and
   * picking whichever was stored first would put half the interns on one schedule and the
   * rest on another depending on list order.
   */
  static findPolicyForEmploymentType(employmentType?: string): NamedLeavePolicy | undefined {
    const wanted = String(employmentType || '').trim().toLowerCase();
    if (!wanted) return undefined;
    const matches = this.getNamedPolicies()
      .filter(p => p.isActive)
      .filter(p => (this.policyEmploymentType(p) || '').toLowerCase() === wanted);
    return matches.length === 1 ? matches[0] : undefined;
  }

  /**
   * The Employment Type a policy governs.
   *
   * The stored field when HR set one. Otherwise read from the NAME, because the policies
   * that already exist were named after the types they were meant for ("Intern Policy",
   * "Full-Time Policy") long before there was a field to say so.
   *
   * Two deliberate restraints on reading the name:
   *
   *   - Exactly one employment type may appear in it. A name mentioning two is ambiguous,
   *     and guessing there puts somebody on the wrong leave schedule.
   *   - The policy must actually grant one of EL/CL/SL. An empty policy — "Consultant
   *     Policy" with every allocation at 0 — would otherwise silently strip the leave its
   *     employment type gets by default, from nothing more than its name. HR can still
   *     point an empty policy at a type on purpose by setting the field.
   *
   * Comparison ignores case, spaces and punctuation, so "Full-Time Policy" finds "Full Time".
   */
  static policyEmploymentType(policy: NamedLeavePolicy): string | undefined {
    const explicit = String(policy?.employmentType || '').trim();
    if (explicit) return explicit;

    const rates = this.ratesFromPolicy(policy);
    if (!rates || rates.el + rates.cl + rates.sl <= 0) return undefined;

    const flatten = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '');
    const name = flatten(String(policy?.name || ''));
    if (!name) return undefined;

    const matches = OrgConfigService.getEmploymentTypes().filter(type => {
      const flat = flatten(type);
      return flat.length > 0 && name.includes(flat);
    });
    return matches.length === 1 ? matches[0] : undefined;
  }

  /**
   * The EL/CL/SL annual caps a policy sets, or undefined when it names none of the three.
   *
   * A code the policy lists at 0 IS a cap of zero, not "unspecified" — the editor writes an
   * allocation row for every leave type, so 0 is what HR left alone and is exactly how an
   * Intern Policy granting only Casual Leave says interns get no earned or sick leave.
   * undefined is reserved for a policy that mentions none of them at all, which must not
   * zero an employee who is only being handed a Maternity or Comp-Off allocation.
   */
  /**
   * The EL/CL/SL caps for one employee, from configuration alone.
   *
   * The Leave Policy governing them if one does, otherwise the Leave Types list. Neither
   * is a number written in code: both are edited in Leave Roster > Leave Policies, which is
   * the point — how much leave somebody gets is a business decision, not a constant.
   *
   * `leaveTypes` may be passed by a caller that already holds the list, so a run across the
   * whole company does not re-read it per employee.
   */
  static ratesForEmployee(
    user: {
      email: string;
      department?: string;
      designation?: string;
      location?: string;
      employmentType?: string;
    },
    leaveTypes?: LeaveTypeMaster[]
  ): LeaveAccrualRates {
    return (
      this.ratesFromPolicy(this.getEffectivePolicyForEmployee(user)) ||
      this.ratesFromLeaveTypes(leaveTypes)
    );
  }

  /**
   * An allocation read as days per earned month.
   *
   * This is what the ACCRUAL column on Leave Policies means. "Casual Leave: 1" on a MONTHLY
   * policy is one day each month; the same 1 on a YEARLY policy is one day for the whole
   * year, so it becomes 1/12 of a day a month. The frequency was stored and displayed but
   * never read, which made the two indistinguishable to every calculation in the product.
   */
  static perMonthRate(value: number, frequency?: AccrualFrequency): number {
    const days = Math.max(0, Number(value) || 0);
    return frequency === 'MONTHLY' ? days : days / 12;
  }

  /**
   * The rates the Leave Types list implies — used when no policy claims the employee.
   *
   * The same grantedPerYear values Maternity and Comp-Off have always accrued from, and
   * annual by definition: a leave type carries no frequency of its own.
   */
  static ratesFromLeaveTypes(leaveTypes?: LeaveTypeMaster[]): LeaveAccrualRates {
    const list = leaveTypes && leaveTypes.length > 0 ? leaveTypes : this.getLeaveTypes();
    const rate = (code: string): number => {
      const type = list.find(t => String(t.code || '').toUpperCase() === code);
      return this.perMonthRate(Number(type?.grantedPerYear) || 0, 'YEARLY');
    };
    return { el: rate('EL'), cl: rate('CL'), sl: rate('SL') };
  }

  /**
   * The rates a policy sets, at its own accrual frequency, or undefined when it names none
   * of EL/CL/SL.
   *
   * A code the policy lists at 0 IS a rate of zero, not "unspecified" — the editor writes an
   * allocation row for every leave type, so 0 is what HR left alone and is exactly how an
   * Intern Policy granting only Casual Leave says interns get no earned or sick leave.
   * undefined is reserved for a policy that mentions none of them at all, which must not
   * zero an employee who is only being handed a Maternity or Comp-Off allocation.
   */
  static ratesFromPolicy(policy?: NamedLeavePolicy): LeaveAccrualRates | undefined {
    if (!policy) return undefined;
    const allocationFor = (code: string): LeavePolicyAllocation | undefined =>
      (policy.allocations || []).find(
        a => String(a.leaveTypeCode || '').toUpperCase() === code
      );
    const el = allocationFor('EL');
    const cl = allocationFor('CL');
    const sl = allocationFor('SL');
    if (!el && !cl && !sl) return undefined;
    const rate = (a?: LeavePolicyAllocation) =>
      this.perMonthRate(Number(a?.grantedPerYear) || 0, policy.accrualFrequency);
    return { el: rate(el), cl: rate(cl), sl: rate(sl) };
  }

  // ─── Organisation-wide settings ───

  static getSettings(): LeaveSettings {
    try {
      const raw = localStorage.getItem(LEAVE_SETTINGS_KEY);
      if (!raw) return DEFAULT_LEAVE_SETTINGS;
      const parsed = JSON.parse(raw);
      // Merged over the defaults so a setting added later does not read as undefined on a
      // browser holding an older stored object.
      const merged: LeaveSettings = { ...DEFAULT_LEAVE_SETTINGS, ...parsed };

      /*
       * Carry a value saved under the old `compOffMaxDays` name onto the field that replaced
       * it. The merge above would otherwise mask it: the default supplies
       * compOffValidityDays = 45, so a stored 20 under the old name would be ignored and HR's
       * configured number would silently revert to the default the moment the field was
       * renamed. Only applied when the new field was genuinely absent from storage.
       */
      if (parsed?.compOffValidityDays === undefined && Number.isFinite(parsed?.compOffMaxDays)) {
        merged.compOffValidityDays = parsed.compOffMaxDays;
      }
      return merged;
    } catch {
      return DEFAULT_LEAVE_SETTINGS;
    }
  }

  static saveSettings(settings: Partial<LeaveSettings>): LeaveSettings {
    const next = { ...this.getSettings(), ...settings };
    // A negative grace period would lock requests before their own start date.
    next.lockPeriodDays = Math.max(0, Math.floor(Number(next.lockPeriodDays) || 0));
    this.persistConfig(LEAVE_SETTINGS_KEY, next);
    window.dispatchEvent(new Event('leave-policy-updated'));
    window.dispatchEvent(new Event('storage'));
    return next;
  }

  /** Convenience read for callers that only need the grace period. */
  static getLockPeriodDays(): number {
    return this.getSettings().lockPeriodDays;
  }
}
